import { codeUnitString } from "./code-unit-string.js";
import { POD_PHASES } from "./pod-phase.js";
import { z } from "zod";
//#region src/scaling-event.ts
/**
* The scaling-event classes. D8's three observed classes PLUS `clamp` — the D6
* "surface" event emitted by the WS-3 operator compiler when it clamps an app's
* intent to an admin limit (DEC-WS3-a / DEC-CL-c: WS-3 emits it via THIS WS-5
* publisher; WS-5 owns the shape). Discriminator on `payload.type`.
*/
const SCALING_EVENT_CLASSES = [
	"node-provisioning",
	"pod-lifecycle",
	"cu-provision",
	"clamp"
];
/** True iff `c` is a known {@link ScalingEventClass}. */
function isScalingEventClass(c) {
	return typeof c === "string" && SCALING_EVENT_CLASSES.includes(c);
}
/**
* Max length, in UTF-16 code units, of any k8s-sourced free-text field (node/pod
* name, reason, message). It is not a byte bound: 512 BMP units can be 1,536 UTF-8
* bytes. k8s names ≤ 253; condition messages can be long. Bounding the input
* BEFORE it reaches a log/query/subject is the ReDoS / log-injection defense
* (CodeQL js/log-injection). Truncation is applied at CAPTURE (operator §3.2),
* and the schema rejects anything longer so a bug fails a test, not prod.
*/
const SCALING_TEXT_MAX = 512;
const scalingText = codeUnitString().max(512);
const scalingTextNullable = scalingText.nullable();
/**
* Attribution — derived by the operator from the reconciled resource's LABELS,
* never from client input (DEC-S4-c). `appScope` is the app slug
* (`zion.witness.ai/app`), the NATS subject token; `version` the versionSlug
* (`zion.witness.ai/version`). `computeUnitHandle` reads no label. The operator sends two values
* for it:
* - `null` on every event that `scaling-event-reconciler.ts` builds from a node snapshot. No pod
*   carries a compute-unit handle since the compute-unit runtime left in stage S5.
* - `<unitKind>/<unit>` on a compute refusal from `refusalToScalingEventBody` in
*   `compute-resolution-reporter.ts`, for example `service/api` or `worker/ingest`. The value names
*   the refused unit so that the clamp dedup key tells two refusals apart (D87(a)). It names no
*   compute unit.
*
* `computeUnitHandle` is optional from 40.0.0 (2026-10-14-app-pools-runtime-deletion, R-075 step 1,
* R-082 item 4). The schema still accepts the key with a string or `null`, so an event from today's
* operator parses unchanged, and it also accepts an event without the key. The operator keeps
* sending it: the dedup key of its clamp events reads it. A later release drops the key only after
* every app that parses scaling events bundles this SDK (R-075 steps 2 and 3).
*/
const scalingAttributionSchema = z.object({
	/** App slug — the `zion.witness.ai/app` label; the NATS subject token. */
	appScope: z.string().min(1).max(63),
	/** versionSlug (`zion.witness.ai/version`); null when unlabelled. */
	version: z.string().max(128).nullable(),
	/** Fixture name (`zion.witness.ai/fixture`); null for non-fixture nodes. */
	fixtureName: z.string().max(253).nullable(),
	/** `<unitKind>/<unit>` on a compute refusal, `null` on a node event, absent is legal (R-075). */
	computeUnitHandle: z.string().max(253).nullable().optional(),
	/** Machine type (`zion.witness.ai/node-type`); null when unknown. */
	nodeType: z.string().max(63).nullable()
}).strict();
/** node-provisioning lifecycle phase (in-cluster OBSERVED effect, DEC-S4-a). */
const NODE_PROVISIONING_PHASES = [
	"scale-up-observed",
	"node-ready",
	"schedulable"
];
const nodeProvisioningPayloadSchema = z.object({
	type: z.literal("node-provisioning"),
	phase: z.enum(NODE_PROVISIONING_PHASES),
	/** The Node observed Ready (`node-ready`); null for pending-pod-derived phases. */
	nodeName: scalingTextNullable,
	/** The pending pod that drove the scale-up observation; null once schedulable. */
	pendingPodName: scalingTextNullable,
	/** PodScheduled condition reason (e.g. `Unschedulable`); null when scheduled. */
	reason: scalingTextNullable,
	/** Truncated k8s condition message (WHY-pending / autoscaler note). Untrusted. */
	message: scalingTextNullable,
	/**
	* SM-5 enrichment — requests the activator is HOLDING for this service while
	* the node boots (= the activator's existing `/admin/signals/<signalsKey>`
	* `httpQueueDepth`, read best-effort by the operator). null when the service
	* is not activator-fronted or the signals read failed. This is the SM-5
	* "held" bit — no new activator endpoint (DEC-WS4-b: uses the existing gauge).
	*/
	heldRequests: z.number().int().min(0).nullable()
}).strict();
/**
* pod-lifecycle phases mirror k8s `status.phase`.
*
* ⛔ **RE-EXPORTED UNDER THIS NAME, not declared here (#1994).** The vocabulary moved DOWN to
* `pod-phase.ts` because three pod-READ schemas need it and importing it upward from an autoscaling
* telemetry module would invert the dependency. The alias keeps this module's public surface — and
* `index.ts`'s barrel entry — byte-unchanged for every existing consumer, with exactly one
* definition surviving. Same shape as `id-grammar.ts`'s `ID_PART_PATTERN`.
*
* ⚠️ `POD_LIFECYCLE_PHASES` is REFERENTIALLY IDENTICAL to `POD_PHASES`, and a test asserts it with
* `toBe`. A re-typed copy with the same members would satisfy any `.length` or member check and
* still drift on the next edit.
*/
const POD_LIFECYCLE_PHASES = POD_PHASES;
const podLifecyclePayloadSchema = z.object({
	type: z.literal("pod-lifecycle"),
	podName: scalingText,
	phase: z.enum(POD_LIFECYCLE_PHASES),
	/** PodScheduled=True. */
	scheduled: z.boolean(),
	/** Ready condition True. */
	ready: z.boolean(),
	/** WHY-pending — PodScheduled/Ready reason when not ready; null otherwise. */
	pendingReason: scalingTextNullable,
	/** Truncated k8s message. Untrusted. */
	message: scalingTextNullable,
	restartCount: z.number().int().min(0)
}).strict();
/** cu-provision lifecycle steps (inline from the CU provisioner, DEC-S4-a). */
const CU_PROVISION_STEPS = [
	"image-resolved",
	"statefulset-created",
	"awaiting-node",
	"pod-scheduled",
	"ready",
	"parked",
	"resumed",
	"decommissioned"
];
const cuProvisionPayloadSchema = z.object({
	type: z.literal("cu-provision"),
	step: z.enum(CU_PROVISION_STEPS),
	outcome: z.enum([
		"started",
		"completed",
		"failed"
	]),
	/** Truncated detail (image ref / failure message). Untrusted. */
	message: scalingTextNullable
}).strict();
/**
* The admin limits an intent can be clamped against (DEC-S5-b/d — hard clamps only).
* `enabledMachineTypes` is the DP's `provisioning.dp_enabled_machine_types` set
* (DEC-DEDICATED-ENABLED, round-2 security B3): WS-3's dedicated-pool compiler now
* REJECTS an intent whose `nodeType` is not in that set and surfaces it as a clamp
* rather than compiling a pool of any machine type the app asks for.
*
* compute-classes D27 adds two:
*   - `resourceCeiling`   — the effective vector (declared × the effective multiplier) exceeded the
*                           RESOLVED machine type's derived allocatable ceilings, so the platform
*                           applied the ceiling instead of the ask. `requested` = the effective
*                           vector's component, `applied` = the ceiling.
*   - `classAvailability` — the declared class had no satisfying rung enabled on this data plane, so
*                           resolution fell back or refused. `requested` = the class asked for,
*                           `applied` = what was used (or the refusal code when nothing was).
* ⭐ `invalid-machine-type` deliberately gets NO new member: it maps onto the existing
* `enabledMachineTypes`, which already means "the intent named a machine type that is not in the
* enabled set". A second spelling for one condition is how two halves of a surface drift.
*
* ⚠️ A clamp event is the NARROWED case only. D9's "every application is evented" is a broader
* obligation this payload cannot carry honestly — an override applied UNCHANGED is not a clamp, and
* emitting one with `requested === applied` would teach every reader that every override is a clamp.
* The unchanged-application record is `StatusReport.computeResolutions` (D19/D24/D38), whose
* `clamped` field is ABSENT on an unclamped row. Do not stretch this payload to cover it.
*
* ⚠️ `requested`/`applied` are already `z.string().max(128)`, so a class name (`'gpu-inference'`,
* 13 bytes), a size (`'.5x'`, 3 bytes) and a quantity all fit — only the two enum members change.
*
* ⚠️ D27's ACCEPTED SKEW, recorded so nobody windows it: the operator publishes these events and
* **DP core-api** reads them back with `z.enum(CLAMP_LIMITS)`. Both ship in the ONE
* `zion-data-plane` chart, so the exposure is a single roll in which a just-restarted operator could
* emit a member a not-yet-restarted core-api rejects. That is a one-roll skew on an observability
* stream, it is EXPLICITLY ACCEPTED (D27), and it is NOT the CP-first hazard of the status-report
* wire — no CP parses this schema. Do not add a dual-accept window here.
*/
const CLAMP_LIMITS = [
	"maxNodesPerPool",
	"allowDedicated",
	"spotPolicy",
	"enabledMachineTypes",
	"resourceCeiling",
	"classAvailability"
];
/**
* A `clamp` event — emitted by the WS-3 operator compiler when it clamps an
* app's declared intent to a DP admin limit (D6 surface; DEC-WS3-a). `requested`
* / `applied` are stringified so one shape covers a numeric cap, a bool, a
* spot-policy enum, and a machine-type string. `poolId` = the dedicated/demand pool
* the clamp applied to; **null when no pool was compiled at all** — the
* `allowDedicated` (dedicated-forbidden) and `enabledMachineTypes` (non-enabled
* nodeType → pool NOT compiled) cases.
*/
const clampPayloadSchema = z.object({
	type: z.literal("clamp"),
	limit: z.enum(CLAMP_LIMITS),
	/** The app's requested value (stringified). */
	requested: z.string().max(128),
	/** The clamped value actually applied (stringified). */
	applied: z.string().max(128),
	/** The pool the clamp affected (WS-3 poolId); null when DP-wide. */
	poolId: scalingTextNullable,
	/** Human-readable detail (e.g. the spot-on-stateful exemption, DEC-S5-e). */
	message: scalingTextNullable
}).strict();
/** Discriminated payload union — one arm per class. */
const scalingEventPayloadSchema = z.discriminatedUnion("type", [
	nodeProvisioningPayloadSchema,
	podLifecyclePayloadSchema,
	cuProvisionPayloadSchema,
	clampPayloadSchema
]);
/**
* The wire body the operator publishes (BEFORE the JetStream sequence is known).
* `class` is redundant with `payload.type` and asserted equal by a refine so a
* mis-tagged event fails at publish, not silently.
*/
const scalingEventBodySchema = z.object({
	dataPlaneId: z.string().min(1).max(253),
	/** ISO-8601 UTC observation time (operator clock). */
	ts: z.string().datetime(),
	class: z.enum(SCALING_EVENT_CLASSES),
	attribution: scalingAttributionSchema,
	payload: scalingEventPayloadSchema
}).strict().refine((e) => e.class === e.payload.type, {
	message: "scaling-event `class` must equal `payload.type`",
	path: ["class"]
});
/**
* The envelope core-api serves after reading from JetStream: the body plus the
* JetStream stream `sequence` (the resume cursor). `sequence` is the JS stream
* seq serialised as a string (bigint-safe), mirroring
* `ProvisioningEventEnvelope.sequence`.
*/
const scalingEventEnvelopeSchema = z.object({ sequence: z.string().min(1) }).and(scalingEventBodySchema);
/**
* REST replay query for `GET /v1/apps/:appId/scaling-events` +
* `GET /v1/admin/scaling-events`. `z.coerce` handles query-string coercion; the
* server caps `limit` independently (§4.3). Mirrors `EventReplayQuery`.
*/
const scalingEventQuerySchema = z.object({
	/** Last JS stream sequence the client has seen; server replays after it. */
	since: z.coerce.number().int().min(0).optional(),
	/** Max events to return; 0/omitted → server default. Server clamps to a ceiling. */
	limit: z.coerce.number().int().min(0).max(1e3).optional(),
	/** Filter to specific classes; omitted → all. */
	class: z.enum(SCALING_EVENT_CLASSES).optional()
}).strict();
/** REST replay response. `truncated` ⇒ more events exist past `nextSince`. */
const scalingEventPageSchema = z.object({
	events: z.array(scalingEventEnvelopeSchema),
	nextSince: z.string().optional(),
	truncated: z.boolean()
}).strict();
/** The activator-nodescale ConfigMap `data.status` JSON (stringified in the CM). */
const activatorNodeScaleStatusSchema = z.object({
	/** The activator per-service key = `<appId>--<serviceName>--<versionSlug>` (RouteSpec.key). */
	signalsKey: z.string().min(1).max(253),
	/** True when the operator observes a pending-Unschedulable pod for this service. */
	nodeScalePending: z.boolean(),
	/** Best-effort epoch-ms the hold may extend to (now + node-boot budget); null = unknown. */
	scaleDeadlineEpochMs: z.number().int().positive().nullable()
}).strict();
//#endregion
export { CLAMP_LIMITS, CU_PROVISION_STEPS, NODE_PROVISIONING_PHASES, POD_LIFECYCLE_PHASES, SCALING_EVENT_CLASSES, SCALING_TEXT_MAX, activatorNodeScaleStatusSchema, clampPayloadSchema, cuProvisionPayloadSchema, isScalingEventClass, nodeProvisioningPayloadSchema, podLifecyclePayloadSchema, scalingAttributionSchema, scalingEventBodySchema, scalingEventEnvelopeSchema, scalingEventPageSchema, scalingEventPayloadSchema, scalingEventQuerySchema };

//# sourceMappingURL=scaling-event.js.map